import { useCallback, useMemo } from 'react';

import { timezoneOptions } from '@/constants/constants';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import { Option, saveUserConfig } from '@/slices/userConfigSlice';

const usePreferences = () => {
  const dispatch = useAppDispatch();
  const userConfigState = useAppSelector(state => state.userConfig);
  const authState = useAppSelector(state => state.auth);
  const {
    userConfig: { timezone, dateFormat, defaultMarket: userDefaultMarket, homepage: userHomepage }
  } = userConfigState;
  const { groupedMarkets, marketsList } = useAppSelector(state => state.marketsList);
  const { showLoadingSnackbar, showErrorSnackbar, showSuccessSnackbar } =
    useSnackbarNotifications();

  const marketsOptions = useMemo(
    () =>
      groupedMarkets.map(group => {
        return {
          label: group.name,
          options: group.markets.map(market => ({
            label: market.name,
            value: market.marketId as string
          }))
        };
      }),
    [groupedMarkets]
  );

  const getFallbackOption = useCallback(
    (userOption: Option): Option => {
      if (userOption.value.length) {
        return userOption;
      }
      const firstAvailable = marketsList[0];
      if (!firstAvailable) {
        return {
          label: '',
          value: ''
        };
      }
      return {
        label: firstAvailable.metadata.displayName,
        value: firstAvailable.marketId
      };
    },
    [marketsList]
  );

  const defaultMarket = useMemo(
    () => getFallbackOption(userDefaultMarket),
    [getFallbackOption, userDefaultMarket]
  );

  const homepage = useMemo(
    () => getFallbackOption(userHomepage),
    [getFallbackOption, userHomepage]
  );

  const changeUserSetting = useCallback(
    (
      settingName: 'timezone' | 'dateFormat' | 'defaultMarket' | 'homepage',
      userSetting: Option
    ) => {
      const config = {
        ...userConfigState.userConfig,
        [settingName]: userSetting
      };
      const key = `preferences-${settingName}`;
      showLoadingSnackbar('Saving your changes, please wait...', key);
      dispatch(saveUserConfig({ userConfig: config }))
        .unwrap()
        .then(() => {
          showSuccessSnackbar('Changes saved!', key);
        })
        .catch(() => {
          showErrorSnackbar(
            "Oops! We couldn't save your changes. Please try again.",
            undefined,
            key
          );
        });
    },
    [
      dispatch,
      showErrorSnackbar,
      showLoadingSnackbar,
      showSuccessSnackbar,
      userConfigState.userConfig
    ]
  );

  return {
    terms: authState.terms,
    timezone,
    dateFormat,
    defaultMarket,
    changeUserSetting,
    timezoneOptions,
    marketsOptions,
    homepage
  };
};

export default usePreferences;
